import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Box, Button, Stack, Typography} from "@mui/material";
import {CheckRounded, PhotoCameraRounded, RefreshRounded, UploadFileRounded} from "@mui/icons-material";

interface CaptureImageProps {
  onConfirm: (dataUri: string) => void;
  disabled?: boolean;
  sampleImage?: string;
  onPreviewChange?: (ready: boolean) => void;
}

const MAX_DIMENSION = 1600;

/**
 * Downscale very large photos before base64 encoding so the scan payload stays
 * reasonable while keeping enough detail for OCR. Falls back to the raw data
 * URL if the browser canvas is unavailable.
 */
function fileToDataUri(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read the selected image.'));
    reader.onload = () => {
      const dataUrl = reader.result as string;
      const img = new Image();
      img.onload = () => {
        try {
          const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
          if (scale >= 1) {
            resolve(dataUrl);
            return;
          }
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            resolve(dataUrl);
            return;
          }
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch {
          resolve(dataUrl);
        }
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  });
}

const CaptureImage = ({onConfirm, disabled, sampleImage, onPreviewChange}: CaptureImageProps) => {
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {onPreviewChange?.(!!preview);}, [preview, onPreviewChange]);

  const handleFile = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setError(null);
    try {
      const dataUri = await fileToDataUri(file);
      setPreview(dataUri);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  return (
    <Box>
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFile}
        style={{display: 'none'}}
        aria-hidden="true"
        tabIndex={-1}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFile}
        style={{display: 'none'}}
        aria-hidden="true"
        tabIndex={-1}
      />

      {preview ? (
        <Stack spacing={2}>
          <Box
            component="img"
            src={preview}
            alt="Receipt preview"
            sx={{
              width: '100%',
              maxHeight: 420,
              objectFit: 'contain',
              borderRadius: 2,
              backgroundColor: 'action.hover',
            }}
          />
          <Stack direction={{xs: 'column', sm: 'row'}} spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshRounded/>}
              onClick={() => setPreview(null)}
              disabled={disabled}
              sx={{minHeight: 44}}
            >
              Retake
            </Button>
            <Button
              data-tour="confirm-photo"
              variant="contained"
              startIcon={<CheckRounded/>}
              onClick={() => onConfirm(preview)}
              disabled={disabled}
              sx={{minHeight: 44, flexGrow: 1}}
            >
              Use this photo
            </Button>
          </Stack>
        </Stack>
      ) : (
        <Stack spacing={2}>
          <Button
            data-tour="take-photo"
            variant="contained"
            size="large"
            startIcon={<PhotoCameraRounded/>}
            onClick={() => sampleImage ? setPreview(sampleImage) : cameraInputRef.current?.click()}
            disabled={disabled}
            sx={{minHeight: 56}}
          >
            Take a photo
          </Button>
          <Button
            variant="outlined"
            size="large"
            startIcon={<UploadFileRounded/>}
            onClick={() => sampleImage ? setPreview(sampleImage) : fileInputRef.current?.click()}
            disabled={disabled}
            sx={{minHeight: 48}}
          >
            Choose from gallery
          </Button>
        </Stack>
      )}

      {error && (
        <Typography color="error" variant="body2" role="alert" sx={{mt: 1}}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CaptureImage;
